import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { eventAPI } from '../../lib/eventApi';
import { useAuth } from '../../contexts/AuthContext';
import { CalendarBlank, Clock, CheckCircle, UserPlus, UsersThree, WarningCircle } from '@phosphor-icons/react';
import SpotsBar from './SpotsBar';
import RegisteredPlayersList from './RegisteredPlayersList';
import SharedPartnerPicker from './SharedPartnerPicker';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

// Player-facing pre-game info card. Shown when status=registration_open
// and the player has not yet self-registered.
//
// Behavior matrix (`allow_self_register` and registration window required):
//   • spots available:  primary "Register as Player" + secondary "Join Sub Pool"
//   • spots full:       only "Join Sub Pool" (Sub Pool is uncapped)
//   • already in:       "You're registered" / "You're in the Sub Pool" card
export default function PlayerJoinPanel({ session, spots, registrations = [], myRegistration, onJoined }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(null); // 'player' | 'sub' | null
  // Phase B — Shared Session: optional partner picker shown above the
  // "Register as Player" CTA. Sub Pool joins never carry a shared partner.
  const [isShared, setIsShared] = useState(false);
  const [sharedProfileId, setSharedProfileId] = useState(null);

  // Gender Slice — derive pre-flight eligibility messaging. Backend re-validates.
  const sessGender = (session.gender || '').toLowerCase();
  const userGender = (user?.gender || '').toLowerCase();
  const expectedGender = { mens: 'male', womens: 'female' }[sessGender] || null;
  const restrictedLabel = sessGender === 'mens' ? "Men's Night" : sessGender === 'womens' ? 'Ladies Night' : null;
  const genderMissing = !!expectedGender && !userGender;
  const genderMismatch = !!expectedGender && !!userGender && userGender !== expectedGender;

  const allowSelf = !!session.allow_self_register;
  const deadline = session.registration_closes_at;
  const deadlinePassed = deadline ? new Date(deadline) < new Date() : false;
  const isFull = (spots?.filled ?? 0) >= (spots?.max ?? 0);
  const isOpen = session.status === 'registration_open';
  const alreadyIn = !!myRegistration;
  const mySub = myRegistration?.role === 'sub';

  let blockReason = null;
  if (!isOpen) blockReason = 'Registration is closed.';
  else if (!allowSelf) blockReason = 'Registration is admin-managed for this session. Contact your club admin to join.';
  else if (deadlinePassed) blockReason = 'Registration deadline has passed.';

  const handleJoin = async (role) => {
    if (busy) return;
    // Phase B — Sub joins never carry a shared partner; ignore the toggle.
    if (role === 'player' && isShared && !sharedProfileId) {
      toast.error('Select your shared partner before registering.');
      return;
    }
    setBusy(role);
    try {
      await eventAPI.selfRegister(
        session.id,
        role,
        role === 'player' && isShared ? sharedProfileId : null,
      );
      toast.success(
        role === 'sub'
          ? "You're in the Sub Pool. The admin will reach out if you're needed."
          : "You're registered. Initial ranks will be set by the admin before week 1.",
      );
      onJoined?.();
    } catch (err) {
      // Gender Slice — surface structured detail codes if the backend
      // returns them so the user understands WHY they can't register.
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.message) {
        toast.error(detail.message);
      } else {
        toast.error(detail || 'Could not register');
      }
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="player-join-panel">
      <section className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-4 space-y-3" data-testid="join-session-details">
        <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">Session details</div>
        <dl className="space-y-2 text-sm">
          <Row icon={CalendarBlank} label="Starts" value={formatDate(session.starts_at)} />
          <Row icon={Clock} label="Registration closes" value={formatDate(session.registration_closes_at)} />
          <Row label="Format" value={`${session.num_courts} courts × ${session.players_per_court}`} />
          <Row label="Weeks" value={session.weeks_planned} />
        </dl>
      </section>

      <section className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-4 space-y-3" data-testid="join-roster-block">
        <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">Roster</div>
        <SpotsBar filled={spots?.filled ?? 0} max={spots?.max ?? 0} />
        {(spots?.subs ?? 0) > 0 && (
          <div className="text-[11px] text-[#6a766e]" data-testid="join-subs-count">
            Sub Pool: {spots.subs}
          </div>
        )}
      </section>

      <RegisteredPlayersList registrations={registrations} />

      {alreadyIn ? (
        <section
          className={
            mySub
              ? "bg-[#fff5e6] border border-[#f4d29c] rounded-2xl p-4 flex items-start gap-2"
              : "bg-[#015e3f]/5 border border-[#015e3f]/30 rounded-2xl p-4 flex items-start gap-2"
          }
          data-testid={mySub ? "already-sub-card" : "already-registered-card"}
        >
          {mySub ? (
            <UsersThree size={20} weight="fill" className="text-[#a8580f] shrink-0 mt-0.5" />
          ) : (
            <CheckCircle size={20} weight="fill" className="text-[#015e3f] shrink-0 mt-0.5" />
          )}
          <div className="text-sm text-[#013523]">
            <div className="font-medium">
              {mySub ? "You're in the Sub Pool" : "You're registered"}
            </div>
            <div className="text-xs text-[#6a766e] mt-0.5">
              {mySub
                ? "The admin will reach out if a sub is needed for a week."
                : "Initial ranks will be set by the admin before week 1."}
            </div>
          </div>
        </section>
      ) : blockReason ? (
        <section className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-2" data-testid="join-blocked-card">
          <WarningCircle size={18} weight="fill" className="text-amber-700 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">{blockReason}</div>
        </section>
      ) : (
        <div className="space-y-2" data-testid="join-cta-stack">
          {isFull ? (
            // Player spots full — Sub Pool only.
            <>
              <div className="bg-[#fff5e6] border border-[#f4d29c] rounded-xl p-3 text-xs text-[#a8580f]" data-testid="join-full-notice">
                Player spots are full, but you can still join the Sub Pool.
              </div>
              <button
                onClick={() => handleJoin('sub')}
                disabled={busy !== null || genderMismatch || genderMissing}
                data-testid="join-sub-pool-btn"
                className="w-full px-4 py-3 rounded-2xl bg-[#015e3f] text-white text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <UsersThree size={16} weight="bold" />
                {busy === 'sub' ? 'Joining…' : 'Join Sub Pool'}
              </button>
            </>
          ) : (
            <>
              {/* Gender Slice — clear, contextual messaging when the user
                  can't register. Two cases: (a) gender missing → link them
                  to Settings; (b) wrong gender → restricted-to-X explainer. */}
              {restrictedLabel && (genderMissing || genderMismatch) && (
                <div
                  className={`rounded-2xl p-3 border ${
                    genderMismatch
                      ? 'bg-red-50 border-red-200 text-red-700'
                      : 'bg-amber-50 border-amber-200 text-amber-800'
                  }`}
                  data-testid="gender-block-notice"
                >
                  <div className="flex items-start gap-2">
                    <WarningCircle size={16} className="mt-0.5 shrink-0" />
                    <div className="text-xs">
                      {genderMissing ? (
                        <>
                          <div className="font-medium">Complete your profile to register</div>
                          <p className="mt-1">
                            This is a {restrictedLabel} session. Please set your gender in{' '}
                            <Link to="/settings" className="underline" data-testid="gender-block-settings-link">
                              Settings
                            </Link>{' '}
                            before registering.
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="font-medium">{restrictedLabel} only</div>
                          <p className="mt-1">
                            This session is restricted to {expectedGender} members.
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Phase B — Shared Session toggle. Only shown for player
                  registration (not Sub Pool). Default = No. */}
              <section
                className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-3 space-y-2"
                data-testid="shared-session-block"
              >
                <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">
                  Are you sharing this session with another member?
                </div>
                <div className="flex gap-2" role="radiogroup" aria-label="Shared session">
                  {[
                    { value: false, label: 'No' },
                    { value: true, label: 'Yes' },
                  ].map((opt) => (
                    <button
                      key={String(opt.value)}
                      type="button"
                      onClick={() => {
                        setIsShared(opt.value);
                        if (!opt.value) setSharedProfileId(null);
                      }}
                      data-testid={`shared-session-${opt.value ? 'yes' : 'no'}`}
                      className={`flex-1 px-3 py-1.5 rounded-lg text-xs border transition ${
                        isShared === opt.value
                          ? 'bg-[#015e3f] text-white border-[#015e3f]'
                          : 'bg-white text-[#013523] border-[#e3dcd1]'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {isShared && (
                  <div className="pt-1">
                    <SharedPartnerPicker
                      sessionId={session.id}
                      value={sharedProfileId}
                      onChange={setSharedProfileId}
                      testidPrefix="shared-session-picker"
                    />
                    <p className="mt-1.5 text-[11px] text-[#6a766e]">
                      Your shared partner will be displayed alongside your name but does not take a separate roster spot.
                    </p>
                  </div>
                )}
              </section>

              <button
                onClick={() => handleJoin('player')}
                disabled={busy !== null || (isShared && !sharedProfileId) || genderMismatch || genderMissing}
                data-testid="join-session-btn"
                className="w-full px-4 py-3 rounded-2xl bg-[#015e3f] text-white text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <UserPlus size={16} weight="bold" />
                {busy === 'player' ? 'Joining…' : 'Register as Player'}
              </button>
              <button
                onClick={() => handleJoin('sub')}
                disabled={busy !== null}
                data-testid="join-sub-pool-btn"
                className="w-full px-4 py-2.5 rounded-2xl border border-[#015e3f] text-[#015e3f] text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-[#015e3f]/5"
              >
                <UsersThree size={16} weight="bold" />
                {busy === 'sub' ? 'Joining…' : 'Join Sub Pool instead'}
              </button>
            </>
          )}
        </div>
      )}

      <p className="text-[11px] text-[#6a766e] text-center px-4">
        By joining, you confirm your availability for the {session.weeks_planned} weekly sessions.
      </p>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-[11px] uppercase tracking-wider text-[#6a766e] flex items-center gap-1">
        {Icon && <Icon size={11} />} {label}
      </dt>
      <dd className="text-sm text-[#013523] text-right">{value || '—'}</dd>
    </div>
  );
}
